/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import moment from "moment";
import DataTable from "react-data-table-component";
import Input from "../../../components/input";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Dialog, DialogBody, Tooltip } from "@material-tailwind/react";

/* ICONS */
import { FiTrash, FiEdit } from "react-icons/fi";
import { IoClose } from "react-icons/io5";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* API IMPORT */
import { deleteServiceById, getServiceList } from "../../../service/api";

const Services = () => {
  // STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [servicesList, setServicesList] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [search, setSearch] = useState("");

  const [deleteId, setDeleteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  /* FUNCTIONS */
  const handlePageChange = (e) => {
    setPage(e);
  };

  const handlePerPageChange = (e) => {
    setSizePerPage(e);
  };

  const getServicesListFunction = () => {
    setLoading(true);
    const params = {
      page: page,
      sizePerPage: sizePerPage,
    };
    if (search) {
      params.search = search;
    }

    getServiceList(params)
      .then((res) => {
        setServicesList(res?.data?.docs);
        setTotalDocs(res?.data?.totalDocs);
      })
      .catch((e) => {
        console.log("Error: ", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* HANDLE DELETE DATA */
  const handleDelete = () => {
    setLoading(true);
    deleteServiceById(deleteId)
      .then((res) => {
        toast.success(res?.message);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message);
      })
      .finally(() => {
        setDeleteModal(false);
        setDeleteId(null);
        getServicesListFunction();
        setLoading(false);
      });
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      grow: 0.3,
      selector: (row, index) => (
        <span className="ml-2 text-sm font-ibm-semibold text-black text-opacity-60">
          {(page - 1) * sizePerPage + index + 1}
        </span>
      ),
    },
    {
      name: "Title",
      grow: 1.5,
      selector: (row) => (
        <p
          className="text-sm font-ibm-semibold text-black"
          style={{ overflowWrap: "anywhere" }}
        >
          {row?.title}
        </p>
      ),
    },
    {
      name: "Slug",
      grow: 1.2,
      selector: (row) => (
        <span
          className="text-sm font-ibm-regular text-greyText"
          style={{ overflowWrap: "anywhere" }}
        >
          /services/{row?.slug}
        </span>
      ),
    },
    {
      name: "Short Description",
      grow: 2,
      selector: (row) => (
        <span
          className="text-sm font-ibm-regular text-greyText leading-relaxed"
          style={{ overflowWrap: "anywhere" }}
        >
          {row?.shortDescription}
        </span>
      ),
    },
    {
      name: "Status",
      grow: 0.5,
      selector: (row) => (
        <span
          className={`text-sm font-ibm-semibold ${
            row?.isActive ? "text-green-600" : "text-red-500"
          }`}
        >
          {row?.isActive ? "Active" : "Hidden"}
        </span>
      ),
    },
    {
      name: "Last Updated",
      selector: (row) => (
        <span className="text-sm font-ibm-regular text-greyText">
          {moment(row?.updatedAt).format("MMM DD, YYYY")}
        </span>
      ),
    },
    {
      name: "Actions",
      grow: 0.5,
      selector: (row) => (
        <div className="flex items-center gap-1">
          <Tooltip content="Edit" className="text-sm font-ibm-semibold">
            <Link to={`/services/edit/${row?._id}`}>
              <Button className="p-2 border border-greyBorder rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5">
                <FiEdit className="w-4 h-4 text-greyText" />
              </Button>
            </Link>
          </Tooltip>
          <Tooltip content="Delete" className="text-sm font-ibm-semibold">
            <Button
              onClick={() => {
                setDeleteId(row?._id);
                setDeleteModal(true);
              }}
              className="p-2 border border-greyBorder rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
            >
              <FiTrash className="w-4 h-4 text-greyText" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  /* USE-EFFECTS */
  useEffect(() => {
    getServicesListFunction();
  }, [page, sizePerPage, search]);

  /* LOADING SCREEN FOR DATATABLE */
  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-ibm-semibold text-black text-center">
          We are fetching data{" "}
        </p>
      </div>
    );
  };

  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          There are no services to display.
        </p>
      </div>
    );
  };

  return (
    <section className="px-0 py-2 xl:p-5 lg:p-5">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Services | WellCare Pharmacy</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="block xl:flex lg:flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Services
            </h1>
            <h2 className="text-base xl:text-lg lg:text-lg font-ibm-regular text-greyText tracking-tight">
              Add, edit, or remove the services shown on the website.
            </h2>
          </div>
          <div className="block xl:flex lg:flex items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
            {/* SEARCHBAR */}
            <div className="w-[40vh]">
              <Input
                type="text"
                placeholder="Search..."
                name="search"
                id="search"
                value={search}
                onChange={(e) => {
                  setSearch(e?.target?.value);
                  setPage(1);
                }}
              />
            </div>
            <Link to="/services/add">
              <Button className="w-full xl:w-auto lg:w-auto whitespace-nowrap mt-2 xl:mt-0 lg:mt-0 shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md text-sm font-ibm-semibold text-white normal-case transition-all duration-300 ease-in-out">
                Add Service
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-5">
          <DataTable
            className="data-table"
            columns={columns}
            data={totalDocs > 0 ? servicesList : []}
            progressPending={loading}
            progressComponent={<LoadingScreen />}
            pagination
            paginationServer
            paginationTotalRows={totalDocs}
            onChangeRowsPerPage={handlePerPageChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoDataScreen />}
          />
        </div>

        {/* DELETE SERVICE */}
        <Dialog
          open={deleteModal}
          handler={() => setDeleteModal(!deleteModal)}
          size="xs"
          animate={{
            mount: { scale: 1, y: 0 },
            unmount: { scale: 0.9, y: -100 },
          }}
        >
          <DialogBody className="p-0">
            <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
              <h5 className="text-lg font-ibm-bold text-black tracking-tight">
                Delete
              </h5>
              <Button
                className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
                onClick={() => setDeleteModal(!deleteModal)}
              >
                <IoClose className="w-4 h-4 text-greyText" />
              </Button>
            </div>
            <div className="p-5 border-b border-authBorder">
              <p className="text-base font-ibm-regular text-black">
                Are you sure you want to remove this service? It will no longer
                be visible on the website.
              </p>
            </div>
            <div className="flex items-center justify-end px-5 py-3">
              <Button
                onClick={handleDelete}
                className="text-sm shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md font-ibm-semibold text-white transition-all duration-300 ease-in-out normal-case"
              >
                {loading ? "Please wait.." : "Confirm, delete"}
              </Button>
            </div>
          </DialogBody>
        </Dialog>
      </div>
    </section>
  );
};

export default Services;
